import { MessageSquare, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { CategoryBadge } from './CategoryBadge';
import { AlertBadge } from './AlertBadge';
import type { OversightConversation, OversightPagination, ReviewStatus, ChatCategory } from '../../types';
import { REVIEW_STATUS_INFO } from '../../types';

interface ConversationTableProps {
  conversations: OversightConversation[];
  pagination: OversightPagination;
  loading: boolean;
  categories: ChatCategory[];
  onRowClick: (conversationId: string) => void;
  onPageChange: (page: number) => void;
}

export function ConversationTable({
  conversations,
  pagination,
  loading,
  categories,
  onRowClick,
  onPageChange,
}: ConversationTableProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: ReviewStatus) => {
    const info = REVIEW_STATUS_INFO[status];
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${info.bgColor} ${info.color}`}>
        {info.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
        <table className="w-full divide-y divide-gray-200 table-fixed dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              {['User', 'Site', 'Protocol', 'Title', 'Msgs', 'Tokens', 'Status', 'Categories', 'Alerts', 'Date'].map(
                (header) => (
                  <th
                    key={header}
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400"
                  >
                    {header}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
            {Array.from({ length: 5 }).map((_, index) => (
              <tr key={index} className="animate-pulse">
                {Array.from({ length: 10 }).map((_, cellIndex) => (
                  <td key={cellIndex} className="px-3 py-4">
                    <div className="h-4 bg-gray-200 rounded w-full dark:bg-gray-700" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
        <MessageSquare size={48} className="mx-auto text-gray-300 mb-4 dark:text-gray-600" />
        <p className="text-sm text-gray-500 dark:text-gray-400">No conversations found matching your filters.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
      <table className="w-full divide-y divide-gray-200 table-fixed dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800/50">
          <tr>
            <th className="w-[14%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              User
            </th>
            <th className="w-[10%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Site
            </th>
            <th className="w-[10%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Protocol
            </th>
            <th className="w-[20%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Title
            </th>
            <th className="w-[6%] px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Msgs
            </th>
            <th className="w-[7%] px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Tokens
            </th>
            <th className="w-[8%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Status
            </th>
            <th className="w-[12%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Categories
            </th>
            <th className="w-[5%] px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Alerts
            </th>
            <th className="w-[8%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Date
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
          {conversations.map((conversation) => (
            <tr
              key={conversation.id}
              onClick={() => onRowClick(conversation.id)}
              className="cursor-pointer transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <td className="px-3 py-3">
                <div className="text-sm font-medium text-gray-900 truncate dark:text-gray-100">
                  {conversation.user_name || 'Unknown'}
                </div>
                <div className="text-xs text-gray-500 truncate dark:text-gray-400">{conversation.user_email}</div>
              </td>
              <td className="px-3 py-3 text-sm text-gray-600 truncate dark:text-gray-300">
                {conversation.site_name || '-'}
              </td>
              <td className="px-3 py-3 text-sm text-gray-600 truncate dark:text-gray-300">
                {conversation.protocol_number || '-'}
              </td>
              <td className="px-3 py-3">
                <div className="text-sm text-gray-900 line-clamp-3 dark:text-gray-100">
                  {conversation.title || 'Untitled'}
                </div>
              </td>
              <td className="px-2 py-3 text-center text-sm text-gray-600 tabular-nums dark:text-gray-300">
                {conversation.message_count}
              </td>
              <td className="px-2 py-3 text-center text-sm text-gray-600 tabular-nums dark:text-gray-300">
                {conversation.total_tokens.toLocaleString()}
              </td>
              <td className="px-2 py-3">{getStatusBadge(conversation.review_status)}</td>
              <td className="px-2 py-3">
                <div className="flex flex-wrap gap-1">
                  {conversation.categories.slice(0, 2).map((categoryName) => {
                    const category = categories.find((c) => c.name === categoryName);
                    return (
                      <CategoryBadge
                        key={categoryName}
                        name={categoryName}
                        color={category?.color}
                        small
                      />
                    );
                  })}
                  {conversation.categories.length > 2 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      +{conversation.categories.length - 2}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-2 py-3 text-center">
                {conversation.alert_count > 0 ? (
                  <AlertBadge count={conversation.alert_count} />
                ) : (
                  <span className="text-gray-400 dark:text-gray-500">-</span>
                )}
              </td>
              <td className="px-2 py-3 text-xs text-gray-500 dark:text-gray-400">
                {formatDate(conversation.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-300">
          Showing {(pagination.page - 1) * pagination.per_page + 1} to{' '}
          {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total}{' '}
          conversations
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="p-2 rounded-lg border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            Page {pagination.page} of {pagination.total_pages}
          </span>
          <button
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.total_pages}
            className="p-2 rounded-lg border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
